import type {
  EventMarker,
  ExternalEventMarker,
  VideoRecording,
} from "../types/video.types";

const FALLBACK_EVENT_TYPE: EventMarker["event_type"] = "annotation";

type UnknownRecord = Record<string, unknown>;

function asArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function toRecord(value: unknown): UnknownRecord | null {
  if (!value || typeof value !== "object") return null;
  return value as UnknownRecord;
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const parsed =
    typeof value === "bigint"
      ? Number(value)
      : typeof value === "string"
        ? Number(value)
        : (value as number);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toNullableNumber(value: unknown): number | undefined {
  const parsed =
    typeof value === "bigint"
      ? Number(value)
      : typeof value === "string"
        ? Number(value)
        : (value as number);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toSafeString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint") {
    return String(value);
  }
  return fallback;
}

function toIsoOrRaw(value: unknown): string | Date {
  if (value instanceof Date) return value;
  if (typeof value === "string") return value;
  return new Date(0).toISOString();
}

function normalizeEventType(value: unknown): EventMarker["event_type"] {
  if (
    value === "seizure_onset" ||
    value === "seizure_offset" ||
    value === "clinical_event" ||
    value === "annotation"
  ) {
    return value;
  }
  return FALLBACK_EVENT_TYPE;
}

function normalizeEventMarker(
  raw: unknown,
  fallbackVideoId: string,
  fallbackId: string,
): EventMarker {
  const item = toRecord(raw) || {};
  const timestampMs = toFiniteNumber(item.timestamp_ms, 0);
  return {
    marker_id: toSafeString(item.marker_id, fallbackId),
    video_id: toSafeString(item.video_id, fallbackVideoId),
    timestamp_ms: timestampMs,
    event_type: normalizeEventType(item.event_type),
    event_label: toSafeString(item.event_label, "Event marker"),
    description:
      typeof item.description === "string" ? item.description : undefined,
    eeg_timestamp: toNullableNumber(item.eeg_timestamp),
    created_by: typeof item.created_by === "string" ? item.created_by : undefined,
    created_at: toIsoOrRaw(item.created_at),
  };
}

function deriveFeedOrder(item: UnknownRecord, fallbackOrder: number): number {
  const directOrder = toNullableNumber(item.feed_order);
  if (directOrder && directOrder > 0) {
    return Math.floor(directOrder);
  }
  return fallbackOrder;
}

function deriveFeedLabel(
  item: UnknownRecord,
  feedOrder: number,
  feedRole: "primary" | "secondary",
): string {
  if (typeof item.feed_label === "string" && item.feed_label.trim().length > 0) {
    return item.feed_label;
  }
  if (feedRole === "primary") return "Primary camera";
  return `Camera ${feedOrder}`;
}

function normalizeVideoRecording(
  raw: unknown,
  fallbackOrder: number,
  hasSecondaryFeed: boolean,
): VideoRecording {
  const item = toRecord(raw) || {};
  const videoId = toSafeString(item.video_id, `video-${fallbackOrder}`);
  const feedOrder = deriveFeedOrder(item, fallbackOrder);
  const feedRole = feedOrder === 1 ? "primary" : "secondary";
  const markerRows = asArray(item.event_markers);

  return {
    video_id: videoId,
    study_id: toSafeString(item.study_id),
    recording_id: typeof item.recording_id === "string" ? item.recording_id : null,
    camera_key: toSafeString(item.camera_key, `camera-${feedOrder}`),
    feed_order: feedOrder,
    feed_label: deriveFeedLabel(item, feedOrder, feedRole),
    feed_role: feedRole,
    has_secondary_feed:
      typeof item.has_secondary_feed === "boolean"
        ? item.has_secondary_feed
        : hasSecondaryFeed,
    filename: toSafeString(item.filename, "video"),
    file_path: toSafeString(item.file_path),
    storage_provider:
      item.storage_provider === "azure" || item.storage_provider === "s3"
        ? item.storage_provider
        : "local",
    storage_url: typeof item.storage_url === "string" ? item.storage_url : undefined,
    file_hash: typeof item.file_hash === "string" ? item.file_hash : undefined,
    duration_seconds: toFiniteNumber(item.duration_seconds, 0),
    frame_rate: toFiniteNumber(item.frame_rate, 30),
    resolution: toSafeString(item.resolution, "unknown"),
    codec: typeof item.codec === "string" ? item.codec : undefined,
    file_size_bytes: toSafeString(item.file_size_bytes, "0"),
    upload_status:
      item.upload_status === "uploading" ||
      item.upload_status === "processing" ||
      item.upload_status === "completed" ||
      item.upload_status === "failed"
        ? item.upload_status
        : "pending",
    upload_progress: Math.max(0, Math.min(100, toFiniteNumber(item.upload_progress, 0))),
    capture_start_datetime:
      item.capture_start_datetime instanceof Date ||
      typeof item.capture_start_datetime === "string"
        ? item.capture_start_datetime
        : null,
    sync_confidence: toNullableNumber(item.sync_confidence),
    thumbnail_path:
      typeof item.thumbnail_path === "string" ? item.thumbnail_path : undefined,
    created_at: toIsoOrRaw(item.created_at),
    updated_at: toIsoOrRaw(item.updated_at),
    uploaded_by: typeof item.uploaded_by === "string" ? item.uploaded_by : undefined,
    study: toRecord(item.study) as VideoRecording["study"],
    event_markers: markerRows.map((marker, index) =>
      normalizeEventMarker(marker, videoId, `${videoId}-marker-${index + 1}`),
    ),
    sync_offsets: asArray(item.sync_offsets).map((offset) => {
      const sync = toRecord(offset) || {};
      return {
        sync_id: toSafeString(sync.sync_id),
        video_id: toSafeString(sync.video_id, videoId),
        study_id: toSafeString(sync.study_id),
        recording_id:
          typeof sync.recording_id === "string" ? sync.recording_id : null,
        offset_ms: toFiniteNumber(sync.offset_ms, 0),
        sync_method:
          sync.sync_method === "auto" ||
          sync.sync_method === "event_based" ||
          sync.sync_method === "led_flash" ||
          sync.sync_method === "timestamp_bootstrap"
            ? sync.sync_method
            : "manual",
        accuracy:
          sync.accuracy === "high" ||
          sync.accuracy === "medium" ||
          sync.accuracy === "low"
            ? sync.accuracy
            : undefined,
        confidence: toNullableNumber(sync.confidence),
        notes: typeof sync.notes === "string" ? sync.notes : undefined,
        created_by: typeof sync.created_by === "string" ? sync.created_by : undefined,
        created_at: toIsoOrRaw(sync.created_at),
      };
    }),
    playback_ready:
      typeof item.playback_ready === "boolean" ? item.playback_ready : undefined,
    transcode_state:
      item.transcode_state === "ready" ||
      item.transcode_state === "queued" ||
      item.transcode_state === "processing" ||
      item.transcode_state === "failed" ||
      item.transcode_state === "unavailable"
        ? item.transcode_state
        : undefined,
    stream_url:
      typeof item.stream_url === "string" ? item.stream_url : undefined,
  };
}

export function normalizeStudyVideoPayload(payload: unknown): VideoRecording[] {
  const root = toRecord(payload);
  const directList = asArray(root?.data ?? payload);
  if (directList.length === 0) return [];

  const preliminary = directList.map((item, index) =>
    normalizeVideoRecording(item, index + 1, directList.length > 1),
  );

  const ordered = [...preliminary].sort((a, b) => {
    const orderA = a.feed_order ?? Number.POSITIVE_INFINITY;
    const orderB = b.feed_order ?? Number.POSITIVE_INFINITY;
    if (orderA !== orderB) return orderA - orderB;

    const captureA = a.capture_start_datetime
      ? new Date(a.capture_start_datetime).getTime()
      : Number.POSITIVE_INFINITY;
    const captureB = b.capture_start_datetime
      ? new Date(b.capture_start_datetime).getTime()
      : Number.POSITIVE_INFINITY;
    if (captureA !== captureB) return captureA - captureB;

    return a.filename.localeCompare(b.filename);
  });

  return selectDistinctStudyVideoFeeds(ordered);
}

function deriveVideoStreamKey(video: VideoRecording): string {
  const fileHash = video.file_hash?.trim();
  if (fileHash) {
    return `hash:${fileHash}`;
  }

  const pathCandidate = video.file_path?.trim() || video.filename?.trim() || "";
  const basename = pathCandidate.split(/[\\/]/).pop()?.toLowerCase() ?? "";
  if (basename) {
    return `file:${basename}`;
  }

  const cameraKey = video.camera_key?.trim();
  if (cameraKey) {
    return `camera:${cameraKey}`;
  }

  return `video:${video.video_id}`;
}

const AUTO_CAMERA_KEY_PATTERN = /^(?:camera-\d+|study:.+:feed:\d+)$/i;

export function parseFilenameCaptureTimeMs(filename: string): number | null {
  const match = filename.match(
    /[_-](\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})\.[^.]+$/i,
  );
  if (!match) return null;

  const [, month, day, year, hour, minute, second] = match;
  const parsed = new Date(
    2000 + Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second),
  ).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

export function getVideoCaptureStartMs(video: VideoRecording): number {
  if (video.capture_start_datetime) {
    const parsed = new Date(video.capture_start_datetime).getTime();
    if (Number.isFinite(parsed)) return parsed;
  }

  const fromFilename = parseFilenameCaptureTimeMs(video.filename);
  if (fromFilename !== null) return fromFilename;

  const createdAt = new Date(video.created_at).getTime();
  return Number.isFinite(createdAt) ? createdAt : Number.POSITIVE_INFINITY;
}

function getVideoCaptureEndMs(video: VideoRecording): number {
  const durationMs = Math.max(0, toFiniteNumber(video.duration_seconds, 0)) * 1000;
  const start = getVideoCaptureStartMs(video);
  return start + (durationMs > 0 ? durationMs : 10 * 60 * 1000);
}

export function deriveCameraGroupKey(video: VideoRecording): string {
  const explicitKey = video.camera_key?.trim();
  if (explicitKey && !AUTO_CAMERA_KEY_PATTERN.test(explicitKey)) {
    return explicitKey;
  }

  const filePath = video.file_path?.trim() ?? "";
  const pathCameraMatch = filePath.match(/[\\/](?:cam|camera)[_\s-]*(\d+)[\\/]/i);
  if (pathCameraMatch?.[1]) {
    return `path-cam:${pathCameraMatch[1]}`;
  }

  const filename = video.filename?.trim() || filePath.split(/[\\/]/).pop() || "";
  const baseName = filename.replace(/\.[^.]+$/, "");
  const timestampPrefixMatch = baseName.match(/^(.+?)[_-](\d{12})$/i);
  if (timestampPrefixMatch?.[1] && timestampPrefixMatch[1].length >= 2) {
    return `name:${timestampPrefixMatch[1].toLowerCase()}`;
  }

  const cameraMatch = filename.match(/(?:cam(?:era)?)[_\s-]*(\d+)/i);
  if (cameraMatch?.[1]) {
    return `cam:${cameraMatch[1]}`;
  }

  return deriveVideoStreamKey(video);
}

export function deriveCameraDisplayName(video: VideoRecording): string {
  if (video.feed_label && !/^(primary camera|camera \d+)$/i.test(video.feed_label)) {
    const timestampLabel = /^\d{2}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}$/.test(
      video.feed_label.trim(),
    );
    if (!timestampLabel) {
      return video.feed_label.trim();
    }
  }

  const groupKey = deriveCameraGroupKey(video);
  if (groupKey.startsWith("name:")) {
    return groupKey.slice(5);
  }
  if (groupKey.startsWith("cam:") || groupKey.startsWith("path-cam:")) {
    return `Camera ${groupKey.split(":")[1]}`;
  }

  const filename = video.filename.replace(/\.[^.]+$/, "");
  const timestampPrefixMatch = filename.match(/^(.+?)[_-](\d{12})$/i);
  if (timestampPrefixMatch?.[1]) {
    return timestampPrefixMatch[1];
  }

  return video.feed_label?.trim() || "Camera feed";
}

function sortSegmentsChronologically(
  segments: VideoRecording[],
): VideoRecording[] {
  return [...segments].sort((a, b) => {
    const startA = getVideoCaptureStartMs(a);
    const startB = getVideoCaptureStartMs(b);
    if (startA !== startB) return startA - startB;
    return a.filename.localeCompare(b.filename);
  });
}

export function groupStudyVideosByCamera(
  videos: VideoRecording[],
): Map<string, VideoRecording[]> {
  const groups = new Map<string, VideoRecording[]>();

  for (const video of videos.filter(isPlayableStudyVideo)) {
    const cameraKey = deriveCameraGroupKey(video);
    const existing = groups.get(cameraKey) ?? [];
    existing.push(video);
    groups.set(cameraKey, existing);
  }

  for (const [cameraKey, segments] of groups) {
    groups.set(cameraKey, sortSegmentsChronologically(segments));
  }

  return groups;
}

export function getCameraGroupSegments(
  videos: VideoRecording[],
  cameraKey: string,
): VideoRecording[] {
  return groupStudyVideosByCamera(videos).get(cameraKey) ?? [];
}

export function resolveCameraSegmentAtTime(
  segments: VideoRecording[],
  targetWallTimeMs: number,
): VideoRecording | null {
  if (!segments.length) return null;

  const sorted = sortSegmentsChronologically(segments);
  const covering = sorted.find((segment) => {
    const start = getVideoCaptureStartMs(segment);
    const end = getVideoCaptureEndMs(segment);
    return targetWallTimeMs >= start && targetWallTimeMs < end;
  });
  if (covering) return covering;

  const readySegments = sorted.filter(isStudyVideoPlaybackReady);
  if (readySegments.length) {
    return readySegments.reduce((best, segment) => {
      const bestDistance = Math.abs(getVideoCaptureStartMs(best) - targetWallTimeMs);
      const segmentDistance = Math.abs(
        getVideoCaptureStartMs(segment) - targetWallTimeMs,
      );
      return segmentDistance < bestDistance ? segment : best;
    });
  }

  if (targetWallTimeMs <= getVideoCaptureStartMs(sorted[0])) {
    return sorted[0];
  }

  return sorted[sorted.length - 1];
}

export function selectDistinctCameraFeeds(
  videos: VideoRecording[],
): VideoRecording[] {
  const dedupedSegments = selectDistinctStudyVideoFeeds(videos);
  const groups = groupStudyVideosByCamera(dedupedSegments);
  const cameraFeeds = [...groups.entries()].map(([cameraKey, segments]) => {
    const readySegment =
      segments.find(isStudyVideoPlaybackReady) ??
      segments.find((segment) => segment.transcode_state !== "failed") ??
      segments[0];
    return {
      ...readySegment,
      camera_key: cameraKey,
      feed_label: deriveCameraDisplayName(readySegment),
    };
  });

  return cameraFeeds
    .sort((a, b) => getVideoCaptureStartMs(a) - getVideoCaptureStartMs(b))
    .map((video, index) => ({
      ...video,
      feed_order: index + 1,
      feed_role: index === 0 ? ("primary" as const) : ("secondary" as const),
      has_secondary_feed: cameraFeeds.length > 1,
    }));
}

export function formatCameraFeedOptionLabel(
  cameraFeed: VideoRecording,
): string {
  const label = deriveCameraDisplayName(cameraFeed);
  const preparingSuffix = !isStudyVideoPlaybackReady(cameraFeed)
    ? " · preparing"
    : "";
  return `${label}${preparingSuffix}`;
}

function rankVideoForDistinctSelection(video: VideoRecording): number {
  let score = 0;
  if (video.playback_ready) score += 1_000;
  if (video.transcode_state === "ready") score += 500;
  if (video.upload_status === "completed") score += 100;
  if (video.file_path?.trim()) score += 50;
  if (video.storage_url?.trim()) score += 25;

  const createdAt = new Date(video.created_at).getTime();
  if (Number.isFinite(createdAt)) {
    score += createdAt / 1_000_000_000_000;
  }

  return score;
}

export function isPlayableStudyVideo(video: VideoRecording): boolean {
  const filePath = video.file_path?.trim() || "";
  const storageUrl = video.storage_url?.trim() || "";
  if (!filePath && !storageUrl) return false;

  // Pending TVS stubs used to store a bare filename with no on-disk bytes.
  // Those must not appear as playable camera feeds.
  if (
    video.upload_status === "pending" &&
    !storageUrl &&
    !filePath.includes("/") &&
    !filePath.includes("\\")
  ) {
    return false;
  }

  return true;
}

export function selectDistinctStudyVideoFeeds(
  videos: VideoRecording[],
): VideoRecording[] {
  const playable = videos.filter(isPlayableStudyVideo);
  const bestByStream = new Map<string, VideoRecording>();

  for (const video of playable) {
    const streamKey = deriveVideoStreamKey(video);
    const existing = bestByStream.get(streamKey);
    if (
      !existing ||
      rankVideoForDistinctSelection(video) >
        rankVideoForDistinctSelection(existing)
    ) {
      bestByStream.set(streamKey, video);
    }
  }

  const distinct = [...bestByStream.values()].sort((a, b) => {
    const orderA = a.feed_order ?? Number.POSITIVE_INFINITY;
    const orderB = b.feed_order ?? Number.POSITIVE_INFINITY;
    if (orderA !== orderB) return orderA - orderB;

    const captureA = a.capture_start_datetime
      ? new Date(a.capture_start_datetime).getTime()
      : Number.POSITIVE_INFINITY;
    const captureB = b.capture_start_datetime
      ? new Date(b.capture_start_datetime).getTime()
      : Number.POSITIVE_INFINITY;
    if (captureA !== captureB) return captureA - captureB;

    return a.filename.localeCompare(b.filename);
  });

  const hasSecondaryFeed = distinct.length > 1;
  return distinct.map((video, index) => ({
    ...video,
    feed_order: index + 1,
    feed_role: index === 0 ? "primary" : "secondary",
    feed_label:
      video.feed_label && video.feed_label.trim().length > 0
        ? video.feed_label
        : index === 0
          ? "Primary camera"
          : `Camera ${index + 1}`,
    has_secondary_feed: hasSecondaryFeed,
  }));
}

export interface NavigationEventMarker {
  id: string;
  timestampMs: number;
  label: string;
  eventType: string;
  source: "video" | "eeg";
}

export function buildNavigationMarkers(
  videoMarkers: EventMarker[],
  externalMarkers: ExternalEventMarker[] = [],
): NavigationEventMarker[] {
  const normalizedVideoMarkers = videoMarkers.map((marker) => ({
    id: marker.marker_id,
    timestampMs: marker.timestamp_ms,
    label: marker.event_label || "Video marker",
    eventType: marker.event_type,
    source: "video" as const,
  }));

  const normalizedExternalMarkers = externalMarkers
    .map((marker) => {
      const source: "video" | "eeg" = marker.source === "video" ? "video" : "eeg";
      return {
        id: marker.id,
        timestampMs: toFiniteNumber(marker.timestampMs, 0),
        label: marker.label || "EEG event",
        eventType: marker.eventType || "annotation",
        source,
      };
    })
    .filter((marker) => Number.isFinite(marker.timestampMs));

  const deduped = new Map<string, NavigationEventMarker>();
  for (const marker of [...normalizedVideoMarkers, ...normalizedExternalMarkers]) {
    if (!deduped.has(marker.id)) {
      deduped.set(marker.id, marker);
    }
  }

  return [...deduped.values()].sort((a, b) => a.timestampMs - b.timestampMs);
}

export function findAdjacentNavigationMarker(
  markers: NavigationEventMarker[],
  currentTimeMs: number,
  direction: "next" | "prev",
): NavigationEventMarker | null {
  if (!markers.length) return null;
  if (direction === "next") {
    return (
      markers.find((marker) => marker.timestampMs > currentTimeMs + 1) || null
    );
  }
  for (let i = markers.length - 1; i >= 0; i -= 1) {
    if (markers[i].timestampMs < currentTimeMs - 1) {
      return markers[i];
    }
  }
  return null;
}

export function formatVideoDurationLabel(durationSeconds: number): string {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return "duration pending";
  }

  if (durationSeconds < 60) {
    return `${Math.round(durationSeconds)} sec`;
  }

  const minutes = Math.round(durationSeconds / 60);
  return `${minutes} min`;
}

export function formatVideoFeedOptionLabel(video: VideoRecording): string {
  const label =
    video.feed_label && !/^(primary camera|camera \d+)$/i.test(video.feed_label)
      ? video.feed_label
      : video.filename.replace(/\.[^.]+$/, "");
  return `${label} (${formatVideoDurationLabel(video.duration_seconds)})`;
}

export function isStudyVideoPlaybackReady(video: VideoRecording): boolean {
  return video.playback_ready === true;
}

export function filterPlayableStudyVideos(
  videos: VideoRecording[],
): VideoRecording[] {
  return selectDistinctStudyVideoFeeds(videos);
}

export function summarizeStudyVideoPlaybackReadiness(videos: VideoRecording[]): {
  total: number;
  readyCount: number;
  isReady: boolean;
  isWarming: boolean;
} {
  const segments = filterPlayableStudyVideos(videos);
  const cameras = selectDistinctCameraFeeds(segments);
  const readyCount = cameras.filter((camera) => {
    const cameraSegments = getCameraGroupSegments(segments, deriveCameraGroupKey(camera));
    return cameraSegments.some(isStudyVideoPlaybackReady);
  }).length;

  return {
    total: cameras.length,
    readyCount,
    isReady: cameras.length > 0 && readyCount === cameras.length,
    isWarming: cameras.length > 0 && readyCount < cameras.length,
  };
}

export function resolveStudyVideoStreamUrl(video: VideoRecording): string {
  if (video.stream_url?.startsWith("/api/")) {
    return video.stream_url;
  }
  if (video.storage_url && /^https?:\/\//i.test(video.storage_url)) {
    return video.storage_url;
  }
  return `/api/video-eeg/${video.video_id}/stream`;
}

